import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getCategoryByKey } from "../api/api";
import {
  createArticle,
  fetchArticleById,
  parseTags,
  updateArticle,
} from "../api/articlesApi";
import Loader from "./Loader";
import "../styles/PostForm.css";

const emptyForm = {
  title: "",
  summary: "",
  description: "",
  tagsInput: "",
  status: true,
  trending: false,
  imageUrl: "",
  imageFile: null,
};

function toFormState(data) {
  return {
    ...emptyForm,
    title: data.title || "",
    summary: data.summary || "",
    description: data.description || "",
    tagsInput: parseTags(data.tags).join(", "),
    status: Boolean(Number(data.status)),
    trending: Boolean(Number(data.trending)),
    imageUrl: data.imageUrl || "",
    imageFile: null,
  };
}

export default function PostForm({ mode }) {
  const { categoryKey, id } = useParams();
  const category = getCategoryByKey(categoryKey);
  const navigate = useNavigate();
  const location = useLocation();
  const passedPost = location.state?.post;

  const [form, setForm] = useState(
    mode === "edit" && passedPost ? toFormState(passedPost) : emptyForm
  );
  const [preview, setPreview] = useState(
    mode === "edit" && passedPost ? passedPost.imageUrl || "" : ""
  );
  const [loading, setLoading] = useState(mode === "edit" && !passedPost);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Already have the post from the list page's route state — no need to refetch.
    if (mode !== "edit" || passedPost || !category) return;
    let cancelled = false;

    setLoading(true);
    fetchArticleById(category.endpoint, id)
      .then((data) => {
        if (cancelled) return;
        setForm(toFormState(data));
        setPreview(data.imageUrl || "");
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || err.message || "Failed to load post."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, category, id, passedPost]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    updateField("imageFile", file);
    setPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!form.summary.trim()) {
      setError("Summary is required.");
      return;
    }
    if (!form.description.trim()) {
      setError("Description is required.");
      return;
    }

    const tags = form.tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim(),
      description: form.description.trim(),
      tags,
      status: form.status,
      trending: form.trending,
      imageFile: form.imageFile,
    };

    setSaving(true);
    try {
      if (mode === "edit") {
        await updateArticle(id, { ...payload, category: category.label });
      } else {
        await createArticle({ ...payload, category: category.label });
      }
      navigate(`/category/${categoryKey}`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Could not save this post. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  if (!category) return <p>Unknown category.</p>;
  if (loading) return <Loader label="Loading post..." />;

  return (
    <form className="postform" onSubmit={handleSubmit}>
      <h2 className="postform-heading">
        {mode === "edit" ? "Edit Post" : "Create Post"} — {category.label}
      </h2>

      {error && <div className="postform-error">{error}</div>}

      <div className="postform-grid">
        <div className="postform-main">
          <label className="postform-label">Title</label>
          <input
            className="postform-input"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            placeholder="Post title"
          />

          <label className="postform-label">Summary</label>
          <textarea
            className="postform-textarea"
            rows={3}
            value={form.summary}
            onChange={(e) => updateField("summary", e.target.value)}
            placeholder="Short summary shown in listings"
          />

          <label className="postform-label">Description</label>
          <textarea
            className="postform-textarea postform-textarea-lg"
            rows={10}
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Full article content"
          />

          <label className="postform-label">Tags (comma separated)</label>
          <input
            className="postform-input"
            value={form.tagsInput}
            onChange={(e) => updateField("tagsInput", e.target.value)}
            placeholder="Finance, Investment, Breaking News"
          />
        </div>

        <div className="postform-side">
          <label className="postform-label">Cover Image</label>
          <div className="postform-image-preview">
            {preview ? (
              <img src={preview} alt="Preview" />
            ) : (
              <span className="postform-image-placeholder">No image</span>
            )}
          </div>
          <input
            type="file"
            onChange={handleImageChange}
            className="postform-file"
          />
          {mode === "edit" && (
            <p className="postform-hint">
              Leave empty to keep the current image.
            </p>
          )}

          <div className="postform-toggle-row">
            <span>Published</span>
            <label className="postform-switch">
              <input
                type="checkbox"
                checked={form.status}
                onChange={(e) => updateField("status", e.target.checked)}
              />
              <span className="postform-switch-slider" />
            </label>
          </div>

          <div className="postform-toggle-row">
            <span>Trending</span>
            <label className="postform-switch">
              <input
                type="checkbox"
                checked={form.trending}
                onChange={(e) => updateField("trending", e.target.checked)}
              />
              <span className="postform-switch-slider" />
            </label>
          </div>
        </div>
      </div>

      <div className="postform-actions">
        <button
          type="button"
          className="postform-btn postform-btn-cancel"
          onClick={() => navigate(`/category/${categoryKey}`)}
        >
          Cancel
        </button>
        <button type="submit" className="postform-btn postform-btn-save" disabled={saving}>
          {saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Publish Post"}
        </button>
      </div>
    </form>
  );
}
